import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';

const ContoModal = ({ isOpen, onClose, conto, onSave, mode = 'create', contiList = [] }) => {
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    stato: 'attivo',
    immagine: '',
    parent: null,
    posizione: 0,
    saldo: 0.00,
    hasTransactions: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (conto && mode === 'edit') {
      setFormData({
        id: conto.id || null,
        name: conto.name || '',
        stato: conto.stato || 'attivo',
        immagine: conto.immagine || '',
        parent: conto.parent || null,
        posizione: conto.posizione || 0,
        saldo: conto.saldo || 0.00,
        hasTransactions: conto.hasTransactions || false
      });
      setImagePreview(conto.immagine || '');
    } else {
      setFormData({
        id: null,
        name: '',
        stato: 'attivo',
        immagine: '',
        parent: conto?.parent || conto?.id || null,
        posizione: 0,
        saldo: 0.00,
        hasTransactions: false
      });
      setImagePreview('');
    }
    setError('');
  }, [conto, mode, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Il nome è obbligatorio');
      return;
    }

    setLoading(true);
    try {
      console.log('MODAL SUBMIT:', {
        formData,
        mode,
        onSaveExists: !!onSave
      });
      await onSave(formData);
      onClose();
    } catch (err) {
      console.error('MODAL ERROR:', err);
      setError(err.message || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('L\'immagine non può superare 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData(prev => ({ ...prev, immagine: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImagePreview('');
    setFormData(prev => ({ ...prev, immagine: '' }));
  };

  // Costruisce opzioni gerarchiche per il select parent
  const buildParentOptions = (conti, level = 0, excludeId = null) => {
    const options = [];
    
    // Prima di tutto: ordina TUTTA la lista per nome
    const sortedConti = [...conti].sort((a, b) => a.name.localeCompare(b.name));
    
    // Filtra solo i conti che possono essere padri (non transazionali)
    const eligibleConti = sortedConti.filter(c => !c.hasTransactions && c.id !== excludeId);
    
    // Aggiungi tutte le opzioni ordinate per nome
    eligibleConti.forEach(node => {
      const level = getNodeLevel(node, sortedConti);
      options.push({
        id: node.id,
        name: '  '.repeat(level) + node.name,
        level: level
      });
    });
    
    return options;
  };
  
  // Funzione per calcolare il livello di un nodo
  const getNodeLevel = (node, conti, currentLevel = 0) => {
    if (!node.parent) return 0;
    
    const parent = conti.find(c => c.id === node.parent);
    if (!parent) return currentLevel;
    
    return getNodeLevel(parent, conti, currentLevel + 1);
  };

  const parentOptions = buildParentOptions(contiList, 0, conto?.id);
  
  // Debug per vedere le opzioni generate
  console.log('PARENT OPTIONS DEBUG:', {
    contiListLength: contiList.length,
    contoId: conto?.id,
    parentOptions: parentOptions.map(opt => ({ id: opt.id, name: opt.name }))
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">
            {mode === 'create' ? 'Nuovo Conto' : 'Modifica Conto'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Campo ID per debug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ID Conto
            </label>
            <input
              type="number"
              value={formData.id || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, id: parseInt(e.target.value) || null }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-gray-50"
              placeholder="Auto-generato in creazione"
              disabled={mode === 'create'}
            />
            <p className="text-xs text-gray-500 mt-1">
              {mode === 'edit' ? 'ID univoco del conto (non modificabile)' : 'Verrà generato automaticamente'}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome Conto *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              placeholder="Es: Banca Intesa"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Conto Padre
            </label>
            <select
              value={formData.parent || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, parent: e.target.value ? parseInt(e.target.value) : null }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
            >
              <option value="">Nessuno (Livello radice)</option>
              {parentOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Seleziona il conto padre per creare una gerarchia. I conti transazionali non possono avere figli.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stato
            </label>
            <select
              value={formData.stato}
              onChange={(e) => setFormData(prev => ({ ...prev, stato: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
            >
              <option value="attivo">Attivo</option>
              <option value="inattivo">Inattivo</option>
              <option value="sospeso">Sospeso</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Posizione
            </label>
            <input
              type="number"
              value={formData.posizione}
              onChange={(e) => setFormData(prev => ({ ...prev, posizione: parseInt(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              placeholder="0 (auto)"
              min="0"
              step="10"
            />
            <p className="text-xs text-gray-500 mt-1">
              Ordinamento dei conti (multiplo di 10). Lascia 0 per posizionamento automatico.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Saldo Corrente
            </label>
            <input
              type="number"
              value={formData.saldo}
              onChange={(e) => setFormData(prev => ({ ...prev, saldo: parseFloat(e.target.value) || 0.00 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              placeholder="0.00"
              step="0.01"
              disabled={mode === 'edit'} // Non editabile in modifica
            />
            <p className="text-xs text-gray-500 mt-1">
              {mode === 'edit' ? 'Il saldo viene calcolato dinamicamente dalle transazioni' : 'Saldo iniziale del conto'}
            </p>
          </div>

          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.hasTransactions || false}
                onChange={(e) => setFormData(prev => ({ ...prev, hasTransactions: e.target.checked }))}
                className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
              />
              <span className="text-sm font-medium text-gray-700">Transazionale</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-8">
              Se spuntato, questo conto può ricevere transazioni dirette e non può avere sottoconti
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Immagine
            </label>
            <div className="space-y-3">
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <label className="cursor-pointer">
                    <span className="text-sm text-gray-600">Clicca per caricare un'immagine</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Salvataggio...' : (mode === 'create' ? 'Crea' : 'Salva')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContoModal;
